import classNames from 'classnames'
import i18next from 'i18next'
import React, {
  CSSProperties,
  isValidElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { useStableId } from '../../utils'
import { getPositionStyles } from '../form-date/dateUtils'
import { FormError } from '../form-error'
import { FormInput } from '../form-input'
import { IconFa } from '../icon-fa'
import { IconLoading } from '../icon-loading'
import { useModalPortalContainer } from '../modal-base'
import styles from './FormSelect.module.css'
import { FormSelectA11yItem } from './FormSelectA11yItem'
import { FormSelectHeaderItem } from './FormSelectHeaderItem'
import { Option, SingleOption } from './Option'

export interface FormSelectA11yProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onSelect'> {
  openAbove?: boolean
  showInFormStyling?: boolean
  label?: string
  alignMenu?: 'left' | 'right'
  error?: boolean
  errorMessage?: ReactNode
  buttonClass?: string
  selected?: string | number
  selectedLabel?: ReactNode
  searchable?: boolean
  listClassName?: string
  onSearchValue?: string
  onClose?: () => void
  onSearch?: (searchTerm: string) => void
  onSelect: (code?: string | number) => void
  loading?: boolean
  showItemTooltip?: boolean
  options: SingleOption[]
  requiredText?: string
  containerClassName?: string
  placeholder?: string
  renderInPortal?: boolean
  showNoneOption?: boolean
  hideOptionsWhenNotVisible?: boolean
}

const NONE_OPTION_CODE = 'none-option'

// Headers are rendered as inert list items but keep their slot in `options`, so every keyboard
// move and every selection has to step past them - otherwise a header code is handed to onSelect.
const isSelectable = (option?: SingleOption) => option !== undefined && option.type !== 'header'

const selectableIndex = (options: SingleOption[], from: number, step: 1 | -1): number | undefined => {
  for (let index = from; index >= 0 && index < options.length; index += step) {
    if (isSelectable(options[index])) return index
  }
  return undefined
}

const clampToSelectable = (options: SingleOption[], index: number, step: 1 | -1) =>
  selectableIndex(options, index, step) ?? selectableIndex(options, index, step === 1 ? -1 : 1) ?? 0

export const FormSelectA11y = ({
  error,
  errorMessage,
  openAbove,
  showInFormStyling,
  placeholder,
  onSelect,
  onSearch,
  searchable: providedSearchable,
  selected,
  selectedLabel,
  alignMenu = 'left',
  listClassName,
  options,
  onSearchValue,
  onClose,
  showItemTooltip,
  containerClassName,
  requiredText = '*',
  renderInPortal,
  disabled,
  ...props
}: FormSelectA11yProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [visualIndex, setVisualIndex] = useState<number>()
  const [searchTerm, setSearchTerm] = useState(onSearchValue ?? '')
  const listRef = useRef<HTMLUListElement>(null)
  const comboboxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchTermTimeoutIdRef = useRef<NodeJS.Timeout>(undefined)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties>({ opacity: 0 })
  const portalContainer = useModalPortalContainer()
  const dropdownListId = useId()
  const errorMessageElId = useId()
  const labelId = useId()

  const toggling = () => {
    if (disabled) return
    if (isOpen) {
      close()
    } else {
      openDropdownList()
    }
  }

  useEffect(() => {
    if (disabled && isOpen) {
      close()
    }
  }, [disabled])

  const close = useCallback(
    (keepFocusOnDropdown = true) => {
      if (isOpen) {
        setIsOpen(false)
        setVisualIndex(undefined)
        setSearchTerm('')
        onSearch?.('')
        onClose?.()
        if (keepFocusOnDropdown) {
          requestAnimationFrame(() => {
            comboboxRef.current?.focus()
          })
        }
      }
    },
    [isOpen, onSearch, onClose]
  )

  const searchable = useMemo(() => {
    if (providedSearchable !== undefined) return providedSearchable
    return options.length >= 10
  }, [providedSearchable, options])

  const filteredOptions = useMemo(() => {
    // if we have an external filter, don't filter anything here
    if (onSearch || !searchable) return options

    return options.filter((option) => {
      const label = optionLabelToString(option.label)
      return label ? label.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()) : true
    })
  }, [options, onSearch, searchable, searchTerm])

  const optionsWithNoneOption = useMemo(() => {
    let showNoneOption =
      props.showNoneOption !== undefined ? props.showNoneOption : !props.required && !props['aria-required']

    if (searchTerm && searchable) {
      // When filtering through input field, don't show none option
      showNoneOption = false
    }
    return showNoneOption
      ? [{ label: i18next.t('glow.none'), code: NONE_OPTION_CODE } as Option, ...filteredOptions]
      : filteredOptions
  }, [filteredOptions, props.showNoneOption, props.required, props['aria-required'], searchable, searchTerm])

  const handleOnSelect = (code?: string | number) => {
    if (code === NONE_OPTION_CODE) {
      onSelect(undefined)
    } else {
      onSelect(code)
    }
  }

  const onSetVisualIndex = (index: number) => {
    setVisualIndex(index)
    requestAnimationFrame(() => {
      const option = optionsWithNoneOption.at(index)
      if (option?.code !== undefined) {
        const optionElement = listRef.current?.querySelector(`#${CSS.escape(`${dropdownListId}-${index}`)}`)
        if (optionElement) {
          optionElement.scrollIntoView({ block: 'nearest' })
        }
      }
    })
  }

  const openDropdownList = () => {
    setIsOpen(true)
    onSetVisualIndex(selectedIndex > -1 ? selectedIndex : clampToSelectable(optionsWithNoneOption, 0, 1))
    if (renderInPortal) {
      const inputRect = comboboxRef?.current?.getBoundingClientRect()
      setMenuStyles({ width: inputRect?.width ?? '100%' })

      requestAnimationFrame(() => {
        if (inputRect && listRef.current) {
          const bodyRect = document.body.getBoundingClientRect()
          const popperRect = listRef.current.getBoundingClientRect()
          setMenuStyles({ ...getPositionStyles(bodyRect, inputRect, popperRect), width: inputRect.width })
        }
      })
    }
  }

  // Externally filtered options (`onSearch`) arrive a render after the visual index was moved,
  // so the index can end up on a header once the new list lands.
  useEffect(() => {
    if (visualIndex !== undefined && !isSelectable(optionsWithNoneOption.at(visualIndex))) {
      setVisualIndex(clampToSelectable(optionsWithNoneOption, visualIndex, 1))
    }
  }, [optionsWithNoneOption, visualIndex])

  const closeIfApplicable = useCallback(
    (event: MouseEvent) => {
      if (!isOpen) return
      if (event.detail === 0) {
        // keypress, handled separately
      } else {
        // mouse event
        if (inputRef.current && event.composedPath().includes(inputRef.current)) {
          // dont close if input is selected
        } else if (listRef.current && comboboxRef.current) {
          if (event.composedPath().includes(listRef.current)) {
            requestAnimationFrame(() => {
              close(false)
            })
          } else if (!event.composedPath().includes(comboboxRef.current)) {
            close(false)
          }
        }
      }
    },
    [close, isOpen]
  )

  const closeOnValueSelectIfApplicable = useCallback(
    (event: KeyboardEvent): any => {
      if (
        listRef.current &&
        event.composedPath().includes(listRef.current) &&
        (event.key === 'Enter' || event.key === 'Escape')
      ) {
        close()
      }
    },
    [close]
  )

  useEffect(() => {
    window.addEventListener('click', closeIfApplicable)
    window.addEventListener('keydown', closeOnValueSelectIfApplicable)

    // Add scroll event listener when the dropdown is open and rendered in portal
    const handleScroll = () => {
      if (isOpen && renderInPortal && comboboxRef?.current && listRef.current) {
        const inputRect = comboboxRef.current.getBoundingClientRect()
        const bodyRect = document.body.getBoundingClientRect()
        const popperRect = listRef.current.getBoundingClientRect()
        setMenuStyles({ ...getPositionStyles(bodyRect, inputRect, popperRect), width: inputRect.width })
      }
    }

    if (renderInPortal) {
      window.addEventListener('scroll', handleScroll, true)
    }

    return () => {
      window.removeEventListener('click', closeIfApplicable)
      window.removeEventListener('keydown', closeOnValueSelectIfApplicable)
      if (renderInPortal) {
        window.removeEventListener('scroll', handleScroll, true)
      }
    }
  }, [closeIfApplicable, closeOnValueSelectIfApplicable, isOpen, renderInPortal])

  const visualOption = useMemo(
    () => (visualIndex !== undefined ? optionsWithNoneOption.at(visualIndex) : undefined),
    [visualIndex, optionsWithNoneOption]
  )

  const comboboxLabel = useMemo(() => {
    if (selected !== undefined && selectedLabel !== undefined) {
      // biome-ignore lint/suspicious/noDoubleEquals: code can vary between string and number, we want to allow both
      const selectedOption = optionsWithNoneOption.find((option) => option.code == selected)
      return selectedOption?.selectedLabel ?? selectedOption?.label ?? selectedLabel
    }
    if (Number.isInteger(visualIndex)) {
      return visualOption?.selectedLabel ?? visualOption?.label
    } else if (selected !== undefined) {
      // biome-ignore lint/suspicious/noDoubleEquals: code can vary between string and number, we want to allow both
      const selectedOption = optionsWithNoneOption.find((option) => option.code == selected)
      return selectedOption?.selectedLabel ?? selectedOption?.label ?? selected
    }
    return placeholder ?? i18next.t('glow.none')
  }, [
    placeholder,
    selected,
    selectedLabel,
    visualIndex,
    optionsWithNoneOption,
    visualOption?.label,
    visualOption?.selectedLabel
  ])

  const visualFocusId = useMemo(() => {
    return Number.isInteger(visualIndex) ? `${dropdownListId}-${visualIndex}` : undefined
  }, [dropdownListId, visualIndex])

  const selectedIndex = useMemo(
    // biome-ignore lint/suspicious/noDoubleEquals: code can vary between string and number, we want to allow both
    () => optionsWithNoneOption.findIndex((option) => option.code == selected),
    [selected, optionsWithNoneOption]
  )

  useEffect(() => {
    return () => {
      clearTimeout(searchTermTimeoutIdRef.current)
    }
  }, [])

  const onSearchTerm = (char: string) => {
    if (searchTerm.length === 0 && !searchable) {
      searchTermTimeoutIdRef.current = setTimeout(() => {
        setSearchTerm('')
      }, 500)
    }
    const newSearchTerm = searchTerm + char.toLowerCase()
    setSearchTerm(newSearchTerm)

    const searchedVisualIndex = optionsWithNoneOption.findIndex(
      (option) =>
        isSelectable(option) && optionLabelToString(option.label)?.toLocaleLowerCase().startsWith(newSearchTerm)
    )

    if (searchedVisualIndex > -1) {
      onSetVisualIndex(searchedVisualIndex)
    }
  }

  const handleSearchField = (searchTerm: string) => {
    setSearchTerm(searchTerm)
    onSearch?.(searchTerm)
  }

  const onComboboxKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    if (!isOpen) {
      if (event.code === 'ArrowDown') {
        openDropdownList()
        event.preventDefault()
      } else if (event.code === 'ArrowUp' || event.code === 'Home') {
        openDropdownList()
        onSetVisualIndex(clampToSelectable(optionsWithNoneOption, 0, 1))
        event.preventDefault()
      } else if (event.code === 'End') {
        openDropdownList()
        onSetVisualIndex(clampToSelectable(optionsWithNoneOption, optionsWithNoneOption.length - 1, -1))
        event.preventDefault()
      } else if (event.code === 'Enter' || event.code === 'Space') {
        openDropdownList()
      } else if (!event.altKey && !event.ctrlKey && event.key.length === 1) {
        openDropdownList()
        onSearchTerm(event.key)
        event.preventDefault()
      }
    } else {
      const index = visualIndex ?? 0
      const selectedOption = optionsWithNoneOption.at(index)
      const canSelect = isSelectable(selectedOption) && !selectedOption?.disabled
      const nextIndex = selectableIndex(optionsWithNoneOption, index + 1, 1)
      const previousIndex = selectableIndex(optionsWithNoneOption, index - 1, -1)
      if (event.code === 'Escape') {
        close()
      } else if (event.code === 'ArrowDown' && nextIndex !== undefined) {
        onSetVisualIndex(nextIndex)
        event.preventDefault()
      } else if (event.code === 'ArrowUp' && event.altKey) {
        if (canSelect) {
          handleOnSelect(visualOption?.code ?? 0)
        }
        close()
        event.preventDefault()
      } else if (event.code === 'ArrowUp' && previousIndex !== undefined) {
        onSetVisualIndex(previousIndex)
        event.preventDefault()
      } else if (canSelect && event.code === 'Tab') {
        handleOnSelect(visualOption?.code ?? 0)
        close(false)
      } else if (canSelect && (event.code === 'Enter' || event.code === 'Space')) {
        handleOnSelect(visualOption?.code ?? 0)
        close()
      } else if (event.code === 'Home') {
        onSetVisualIndex(clampToSelectable(optionsWithNoneOption, 0, 1))
        event.preventDefault()
      } else if (event.code === 'End') {
        onSetVisualIndex(clampToSelectable(optionsWithNoneOption, optionsWithNoneOption.length - 1, -1))
        event.preventDefault()
      } else if (event.code === 'PageUp') {
        onSetVisualIndex(clampToSelectable(optionsWithNoneOption, Math.max(index - 10, 0), -1))
      } else if (event.code === 'PageDown') {
        onSetVisualIndex(
          clampToSelectable(optionsWithNoneOption, Math.min(index + 10, optionsWithNoneOption.length - 1), 1)
        )
        event.preventDefault()
      } else if (!event.altKey && !event.ctrlKey && event.key.length === 1) {
        onSearchTerm(event.key)
        event.preventDefault()
      }
    }
  }

  const onFocus = () => {
    setIsFocused(true)
  }

  const onBlur = () => {
    setIsFocused(false)
  }

  return (
    <div className={classNames(containerClassName, showInFormStyling && styles['show-in-form'])}>
      {props.label && (
        <label
          className={classNames(styles.labelText, error && styles.error, isFocused && styles.focus)}
          id={labelId}
          data-focused={isFocused}
          aria-hidden
        >
          {props.label}
          {(props['aria-required'] || props.required) && requiredText}
        </label>
      )}
      <div className={classNames(props.className, styles['form-select'])} style={props.style}>
        <div
          id={props.id}
          role="combobox"
          aria-labelledby={labelId}
          aria-required={props['aria-required'] || props.required}
          aria-invalid={error}
          onFocus={onFocus}
          onBlur={onBlur}
          data-focused={isFocused}
          aria-expanded={isOpen}
          aria-describedby={errorMessageElId}
          aria-haspopup="listbox"
          aria-label={props['aria-label']}
          aria-activedescendant={visualFocusId}
          aria-controls={dropdownListId}
          className={classNames(
            styles['dropdown-button'],
            'hover-aa-supported',
            error && styles.hasError,
            disabled && 'opacity-30 pointer-events-none',
            props.buttonClass
          )}
          onKeyDown={onComboboxKeyDown}
          ref={comboboxRef}
          onClick={toggling}
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : 0}
        >
          <span className="text-truncate">{comboboxLabel}</span>
          <IconFa icon={isOpen ? ['far', 'chevron-up'] : ['far', 'chevron-down']} />
        </div>
        {renderInPortal && isOpen ? (
          createPortal(
            <AccessibleDropdownList
              isOpen={isOpen}
              id={dropdownListId}
              openAbove={openAbove}
              alignMenu={alignMenu}
              listRef={listRef}
              visualIndex={visualIndex}
              searchable={searchable}
              style={menuStyles}
              onSearch={handleSearchField}
              searchTerm={searchTerm}
              setVisualIndex={onSetVisualIndex}
              renderInPortal
              className={listClassName}
              inputRef={inputRef}
              selectedIndex={selectedIndex}
              options={optionsWithNoneOption}
              showItemTooltip={showItemTooltip}
              onSelect={handleOnSelect}
              onClose={close}
              loading={props.loading}
              hideOptionsWhenNotVisible={props.hideOptionsWhenNotVisible}
            />,
            portalContainer
          )
        ) : (
          <AccessibleDropdownList
            isOpen={isOpen}
            id={dropdownListId}
            openAbove={openAbove}
            alignMenu={alignMenu}
            listRef={listRef}
            visualIndex={visualIndex}
            searchable={searchable}
            onSearch={handleSearchField}
            searchTerm={searchTerm}
            setVisualIndex={onSetVisualIndex}
            className={listClassName}
            inputRef={inputRef}
            selectedIndex={selectedIndex}
            options={optionsWithNoneOption}
            showItemTooltip={showItemTooltip}
            onSelect={handleOnSelect}
            onClose={close}
            loading={props.loading}
            hideOptionsWhenNotVisible={props.hideOptionsWhenNotVisible}
          />
        )}
      </div>
      <FormError id={errorMessageElId} showError={error} errorMessage={errorMessage} />
    </div>
  )
}

interface AccessibleDropdownList {
  options: SingleOption[]
  id: string
  openAbove?: boolean
  isOpen: boolean
  alignMenu?: 'left' | 'right'
  visualIndex?: number
  selectedIndex?: number
  setVisualIndex?: (index: number) => void
  listRef?: React.RefObject<HTMLUListElement | null>
  inputRef?: React.RefObject<HTMLInputElement | null>
  className?: string
  searchable?: boolean
  searchTerm?: string
  onSearch?: (value: string) => void
  onClose: (keepFocusOnButton?: boolean) => void
  onSelect: (code?: string | number) => void
  showItemTooltip?: boolean
  style?: CSSProperties
  renderInPortal?: boolean
  loading?: boolean
  hideOptionsWhenNotVisible?: boolean
}

export const AccessibleDropdownList = ({
  id,
  openAbove,
  alignMenu,
  listRef,
  options,
  isOpen,
  visualIndex,
  selectedIndex,
  className,
  inputRef,
  searchable,
  showItemTooltip,
  searchTerm,
  style,
  renderInPortal,
  loading,
  setVisualIndex,
  onSearch,
  onClose,
  onSelect,
  hideOptionsWhenNotVisible
}: AccessibleDropdownList) => {
  const key = useStableId()
  const localId = id || key

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (visualIndex === undefined) return
    const visualOption = options.at(visualIndex)
    const selectableCode = isSelectable(visualOption) && !visualOption?.disabled ? visualOption?.code : undefined
    if (event.key === 'ArrowUp' && visualIndex > 0) {
      const previousIndex = selectableIndex(options, visualIndex - 1, -1)
      if (previousIndex !== undefined) {
        setVisualIndex?.(previousIndex)
      }
      event.preventDefault()
    } else if (event.key === 'ArrowDown' && visualIndex + 1 < options.length) {
      const nextIndex = selectableIndex(options, visualIndex + 1, 1)
      if (nextIndex !== undefined) {
        setVisualIndex?.(nextIndex)
      }
      event.preventDefault()
    } else if (event.key === 'Enter') {
      if (selectableCode !== undefined) {
        onSelect(selectableCode)
        onClose()
      }
      event.preventDefault()
      event.stopPropagation()
    } else if (event.key === 'Escape') {
      onClose()

      event.preventDefault()
      event.stopPropagation()
    } else if (event.key === 'Tab') {
      if (selectableCode !== undefined) {
        onSelect(selectableCode)
      }
      onClose(false)
    }
  }

  const handleSearch = (event: React.FormEvent<HTMLInputElement>) => {
    setVisualIndex?.(clampToSelectable(options, 0, 1))
    onSearch?.((event.target as HTMLInputElement).value)
  }

  useEffect(() => {
    if (isOpen && searchable) {
      const timeoutId = setTimeout(() => {
        inputRef?.current?.focus()
      }, 50)
      return () => clearTimeout(timeoutId)
    }
  }, [isOpen])

  const renderOptions = !hideOptionsWhenNotVisible || (hideOptionsWhenNotVisible && isOpen)

  return (
    <ul
      id={localId}
      className={classNames(
        styles['dropdown-list'],
        !isOpen && 'hidden',
        'w-full sm:min-w-min',
        'drop-shadow-02',
        renderInPortal && 'z-1002!',
        openAbove ? styles['position-above'] : null,
        alignMenu === 'right' ? 'right-0' : null,
        className
      )}
      role="listbox"
      ref={listRef}
      style={style}
      key={key}
    >
      <>
        {searchable && (
          <div className={styles.searchable}>
            <FormInput
              hideLabel
              autoComplete="off"
              value={searchTerm}
              onChange={handleSearch}
              onKeyDown={handleSearchKeyDown}
              ref={inputRef}
              icon={loading ? <IconLoading /> : <IconFa icon={['far', 'search']} />}
            />
          </div>
        )}
        {renderOptions &&
          options.map((option, index) =>
            option.type === 'header' ? (
              <FormSelectHeaderItem key={`${option.type}${index}`}>{option.label}</FormSelectHeaderItem>
            ) : (
              <FormSelectA11yItem
                key={`${index}-${option.code}`}
                id={`${id}-${index}`}
                showItemTooltip={showItemTooltip}
                value={option.code}
                disabled={option.disabled}
                isVisuallyFocused={visualIndex === index}
                isSelected={index === selectedIndex}
                onSelect={() => onSelect(option.code)}
              >
                {option.label}
              </FormSelectA11yItem>
            )
          )}
      </>
    </ul>
  )
}

const optionLabelToString = (label: React.ReactNode) =>
  (isValidElement<{ label?: React.ReactNode }>(label)
    ? label.props?.label?.toLocaleString()
    : label?.toLocaleString()) as string | undefined
